import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TouchableOpacity,
  Dimensions,
  Animated,
  ActivityIndicator,
} from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/RestyleIcon';
import { SelectableDndKitListWeb } from '@components/shared/SelectableDndKitList.web';
import { TaskGroup } from '@graphql/generated';
import theme from '@themes/theme';

const windowHeight = Dimensions.get('window').height;

type AnimatedSection = {
  height: Animated.Value;
  opacity: Animated.Value;
  rotation: Animated.Value;
  id: string;
  isOpen: boolean;
};

type SectionData<SectionHeaderType, DataType> = {
  sectionHeader: SectionHeaderType;
  data: DataType[];
  // Optional pagination info (used by MyTaskList via "as any")
  hasNextPage?: boolean;
  loading?: boolean;
};

type CollapsableSectionListProps<SectionHeaderType, DataType> = {
  renderSectionHeader: (item: SectionHeaderType) => ReactNode;
  data: SectionData<SectionHeaderType, DataType>[];
  renderItem: (
    item: DataType,
    isTheLastOne: boolean,
    isSelected: boolean
  ) => ReactNode;
  renderNewTaskItem: () => ReactNode;
  renderEmptyComponent?: (id: string) => ReactNode;
  // Rendered instead of the default ActivityIndicator while a section's own
  // `loading` is true and it has no data yet (e.g. skeleton rows). Falls
  // back to the built-in spinner when omitted.
  renderSectionLoadingComponent?: (item: SectionHeaderType) => ReactNode;
  renderSectionFooterComponent?: (item: SectionHeaderType) => ReactNode;
  renderSectionContentHeader?: (item: SectionHeaderType) => ReactNode;
  showEmptyMessage?: boolean;
  // If true, only one section can be open at a time
  accordionMode?: boolean;
  // Optional global header/footer
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null;
  ListFooterComponent?: React.ComponentType<any> | React.ReactElement | null;

  onRightMenuAction: (
    actionType:
      | 'complete'
      | 'assignee'
      | 'duplicate'
      | 'moveUp'
      | 'moveDown'
      | 'delete',
    selectedData: DataType[]
  ) => void | Promise<void>;

  // Allow returning Promise so we can stop loading when finished
  onSectionEndReached?: (
    sectionHeader: SectionHeaderType
  ) => void | Promise<void>;
  // If true, section content header (e.g. table header) will stick to the top when scrolling
  stickySectionContentHeader?: boolean;
  // Disable context menu (right-click menu)
  disableContextMenu?: boolean;
  // Array of section IDs that should be expanded initially
  initialExpandedSectionIds?: string[];
  itemHeight?: number;
  // Optional bottom margin inside the scrollable content area
  contentBottomMargin?: keyof typeof theme.spacing;

  draggable?: boolean;
  onOrderChange?: (payload: {
    sectionHeader: SectionHeaderType;
    oldData: DataType[];
    newData: DataType[];
    movedIds: string[];
    afterId: string | null;
    beforeId: string | null;
  }) => void | Promise<void>;
};

type SectionsById = Record<string, AnimatedSection>;
type HeightsById = Record<string, number>;

export const CollapsableSectionListV3 = <
  SectionHeaderType extends { id: string },
  DataType extends { id: string },
>(
  props: CollapsableSectionListProps<SectionHeaderType, DataType>
) => {
  const {
    renderSectionHeader,
    data,
    renderItem,
    renderNewTaskItem,
    renderEmptyComponent,
    renderSectionLoadingComponent,
    renderSectionFooterComponent,
    renderSectionContentHeader,
    showEmptyMessage = true,
    accordionMode = true,
    ListHeaderComponent,
    ListFooterComponent,
    onSectionEndReached,
    stickySectionContentHeader = false,
    onRightMenuAction,
    disableContextMenu = false,
    initialExpandedSectionIds,
    itemHeight = 56,
    contentBottomMargin = 'xs',
    draggable = false,
    onOrderChange,
  } = props;
  const [orderedSectionDataById, setOrderedSectionDataById] = useState<
    Record<string, DataType[]>
  >({});

  // Track if "load more" has been triggered for each section while user stays near bottom
  const loadMoreTriggeredRef = useRef<Record<string, boolean>>({});

  useEffect(() => {
    setOrderedSectionDataById((prev) => {
      let changed = false;
      const next = { ...prev };
      const validSectionIds = new Set(
        data.map((section) => section.sectionHeader.id)
      );

      Object.keys(next).forEach((sectionId) => {
        if (!validSectionIds.has(sectionId)) {
          delete next[sectionId];
          changed = true;
        }
      });

      data.forEach((section) => {
        const sectionId = section.sectionHeader.id;
        const override = next[sectionId];
        if (!override) return;

        const propIds = section.data.map((item) => item.id).sort();
        const overrideIds = override.map((item) => item.id).sort();
        if (
          propIds.length !== overrideIds.length ||
          propIds.some((id, index) => id !== overrideIds[index])
        ) {
          delete next[sectionId];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [data]);

  // During animation, block new toggles
  const isAnimatingRef = useRef(false);

  // Track which sections are currently loading more data
  const [loadingSections, setLoadingSections] = useState<
    Record<string, boolean>
  >({});

  // Track which sections have ever been opened (avoid rendering heavy content for never-opened sections)
  const [openedSections, setOpenedSections] = useState<Record<string, boolean>>(
    {}
  );

  const [containerHeight, setContainerHeight] = useState<number | null>(null);

  // Use dictionaries to avoid repeated .find() on every render
  const [sectionsById, setSectionsById] = useState<SectionsById>({});
  const [heightsById, setHeightsById] = useState<HeightsById>({});

  const [hoveredHeaderId, setHoveredHeaderId] = useState<string | undefined>(
    undefined
  );

  // Track header heights for each section (for renderSectionContentHeader)
  const [sectionHeaderHeights, setSectionHeaderHeights] = useState<
    Record<string, number>
  >({});

  // Bump when user collapses/expands a section so height-sync effect re-runs
  // and remaining open sections can fill the space (maxHeightPerSection recalculated)
  const [openStateVersion, setOpenStateVersion] = useState(0);

  // A stable signature: id:length:hasNextPage to reset loadMore triggers properly
  const sectionLenKey = useMemo(() => {
    return data
      .map((s) => `${s.sectionHeader.id}:${s.data.length}:${!!s.hasNextPage}`)
      .join('|');
  }, [data]);

  useEffect(() => {
    data.forEach((s) => {
      loadMoreTriggeredRef.current[s.sectionHeader.id] = false;
    });
  }, [sectionLenKey, data]);

  /**
   * Returns the max height allowed for the expanded section content.
   * We cap the expanded area so headers remain visible.
   */
  const getMaxContentHeight = useCallback((): number => {
    const base =
      containerHeight && containerHeight > 0
        ? containerHeight
        : windowHeight - 138;

    // Keep the previous behavior: subtract a fixed offset.
    return base - 212;
  }, [containerHeight]);

  /**
   * Sync animated sections and target heights when data / container height changes.
   * NOTE: We intentionally avoid depending on sectionsById/heightsById to prevent update loops.
   */
  // useEffect(() => {
  //   if (data.length === 0 || containerHeight === null) return;
  //
  //   const maxContentHeight = getMaxContentHeight();
  //
  //   setHeightsById((prevHeights) => {
  //     const nextHeights: HeightsById = { ...prevHeights };
  //
  //     setSectionsById((prevSections) => {
  //       const nextSections: SectionsById = { ...prevSections };
  //
  //       data.forEach((item, index) => {
  //         const { sectionHeader, data: sectionData } = item;
  //         const id = sectionHeader.id;
  //
  //         // Estimate content height based on row count.
  //         const estimatedHeight = Math.max(
  //           sectionData.length * 56 + (id === TaskGroup.Current ? 56 : 0),
  //           68
  //         );
  //         const clampedHeight = Math.min(estimatedHeight, maxContentHeight);
  //
  //         const existingSection = prevSections[id];
  //
  //         // First time init: open the first section by default, others closed.
  //         const openByDefault = !existingSection
  //           ? index === 0
  //           : existingSection.isOpen;
  //         const isOpen = existingSection
  //           ? existingSection.isOpen
  //           : openByDefault;
  //
  //         // Keep Animated.Value instances stable across renders.
  //         const heightValue =
  //           existingSection?.height ??
  //           new Animated.Value(isOpen ? clampedHeight : 0);
  //         const opacityValue =
  //           existingSection?.opacity ?? new Animated.Value(isOpen ? 1 : 0);
  //         const rotationValue =
  //           existingSection?.rotation ?? new Animated.Value(isOpen ? 1 : 0);
  //
  //         // If the section is currently open and target height changed, sync immediately.
  //         const prevTarget = prevHeights[id];
  //         if (
  //           isOpen &&
  //           typeof prevTarget === 'number' &&
  //           prevTarget !== clampedHeight
  //         ) {
  //           heightValue.setValue(clampedHeight);
  //         }
  //
  //         nextHeights[id] = clampedHeight;
  //         nextSections[id] = {
  //           id,
  //           isOpen,
  //           height: heightValue,
  //           opacity: opacityValue,
  //           rotation: rotationValue,
  //         };
  //       });
  //
  //       return nextSections;
  //     });
  //
  //     return nextHeights;
  //   });
  // }, [data, containerHeight, getMaxContentHeight]);

  // ✅ add: refs to always access latest states without nesting setState
  const sectionsByIdRef = useRef<SectionsById>({});
  const heightsByIdRef = useRef<HeightsById>({});

  useEffect(() => {
    sectionsByIdRef.current = sectionsById;
  }, [sectionsById]);

  useEffect(() => {
    heightsByIdRef.current = heightsById;
  }, [heightsById]);

  /**
   * ✅ Sync animated sections and target heights when data / container height changes.
   * IMPORTANT: do NOT call setSectionsById inside setHeightsById updater.
   */
  useEffect(() => {
    if (data.length === 0 || containerHeight === null) return;

    const maxContentHeight = getMaxContentHeight();

    const prevSections = sectionsByIdRef.current;
    const prevHeights = heightsByIdRef.current;

    const nextSections: SectionsById = { ...prevSections };
    const nextHeights: HeightsById = { ...prevHeights };

    const nextIds = new Set<string>();

    // First pass: compute estimated heights and which sections are open
    const sectionMeta: Array<{
      id: string;
      estimatedHeight: number;
      isOpen: boolean;
      existingSection: AnimatedSection | undefined;
    }> = [];

    data.forEach((item, index) => {
      const { sectionHeader, data: sectionData } = item;
      const id = sectionHeader.id;

      nextIds.add(id);

      const headerHeight = renderSectionContentHeader
        ? sectionHeaderHeights[id] || 50
        : 0;
      const emptyComponentHeight =
        sectionData.length === 0 && showEmptyMessage && renderEmptyComponent
          ? 96
          : 0;
      const estimatedHeight = Math.max(
        sectionData.length * itemHeight +
          (id === TaskGroup.Current ? itemHeight : 0) +
          headerHeight +
          emptyComponentHeight,
        68
      );

      const existingSection = prevSections[id];
      const openByDefault = !existingSection
        ? (initialExpandedSectionIds?.includes(id) ?? index === 0)
        : existingSection.isOpen;
      const isOpen = existingSection ? existingSection.isOpen : openByDefault;

      sectionMeta.push({ id, estimatedHeight, isOpen, existingSection });
    });

    // Count expanded sections so we can split maxContentHeight between them
    // (avoids total content overflowing viewport so inner section lists can scroll to bottom)
    const numExpanded = sectionMeta.filter((m) => m.isOpen).length;
    const maxHeightPerSection =
      numExpanded > 0 ? maxContentHeight / numExpanded : maxContentHeight;

    sectionMeta.forEach(({ id, estimatedHeight, isOpen, existingSection }) => {
      const clampedHeight = isOpen
        ? Math.min(estimatedHeight, maxHeightPerSection)
        : Math.min(estimatedHeight, maxContentHeight);

      const heightValue =
        existingSection?.height ??
        new Animated.Value(isOpen ? clampedHeight : 0);
      const opacityValue =
        existingSection?.opacity ?? new Animated.Value(isOpen ? 1 : 0);
      const rotationValue =
        existingSection?.rotation ?? new Animated.Value(isOpen ? 1 : 0);

      const prevTarget = prevHeights[id];
      const currentAnimatedHeight =
        typeof existingSection?.height?.__getValue === 'function'
          ? existingSection.height.__getValue()
          : undefined;
      if (
        isOpen &&
        (prevTarget !== clampedHeight ||
          currentAnimatedHeight !== clampedHeight)
      ) {
        heightValue.setValue(clampedHeight);
      }

      nextHeights[id] = clampedHeight;
      nextSections[id] = {
        id,
        isOpen,
        height: heightValue,
        opacity: opacityValue,
        rotation: rotationValue,
      };
    });

    // ✅ optional cleanup: remove stale sections that no longer exist in data
    Object.keys(nextSections).forEach((id) => {
      if (!nextIds.has(id)) delete nextSections[id];
    });
    Object.keys(nextHeights).forEach((id) => {
      if (!nextIds.has(id)) delete nextHeights[id];
    });

    // ✅ set separately (no nesting)
    setHeightsById(nextHeights);
    setSectionsById(nextSections);
  }, [
    data,
    containerHeight,
    getMaxContentHeight,
    renderSectionContentHeader,
    sectionHeaderHeights,
    showEmptyMessage,
    renderEmptyComponent,
    itemHeight,
    initialExpandedSectionIds,
    openStateVersion,
  ]);

  const getRotationStyle = useCallback(
    (id: string) => {
      const section = sectionsById[id];
      if (!section) return undefined;

      return {
        transform: [
          {
            rotate: section.rotation.interpolate({
              inputRange: [0, 1],
              outputRange: ['0deg', '180deg'],
            }),
          },
        ],
      };
    },
    [sectionsById]
  );

  const getAnimatedContentStyle = useCallback(
    (id: string) => {
      const section = sectionsById[id];
      if (!section) return undefined;

      return {
        height: section.height,
        opacity: section.opacity,
        overflow: 'hidden' as const,
      };
    },
    [sectionsById]
  );

  /**
   * Toggle a section open/closed with animation.
   * In accordion mode, close all other sections when opening a new one.
   */
  const animate = useCallback(
    (id: string) => {
      if (isAnimatingRef.current) return;

      const current = sectionsById[id];
      if (!current) return;

      isAnimatingRef.current = true;

      const isOpen = current.isOpen;
      const animations: Animated.CompositeAnimation[] = [];

      // Prepare next state snapshot (do not mutate existing objects directly).
      const nextSections: SectionsById = { ...sectionsById };

      if (accordionMode) {
        Object.values(sectionsById).forEach((s) => {
          if (s.id !== id && s.isOpen) {
            animations.push(
              Animated.timing(s.height, {
                toValue: 0,
                duration: 200,
                useNativeDriver: false,
              }),
              Animated.timing(s.opacity, {
                toValue: 0,
                duration: 200,
                useNativeDriver: false,
              }),
              Animated.timing(s.rotation, {
                toValue: 0,
                duration: 200,
                useNativeDriver: false,
              })
            );

            nextSections[s.id] = { ...nextSections[s.id], isOpen: false };
          }
        });
      }

      const targetHeight = isOpen ? 0 : (heightsById[id] ?? 0);
      const targetOpacity = isOpen ? 0 : 1;
      const targetRotation = isOpen ? 0 : 1;

      // Mark section as "ever opened" to enable heavy content rendering later.
      if (!isOpen) {
        setOpenedSections((prev) =>
          prev[id] ? prev : { ...prev, [id]: true }
        );
      }

      animations.push(
        Animated.timing(current.height, {
          toValue: targetHeight,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(current.opacity, {
          toValue: targetOpacity,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(current.rotation, {
          toValue: targetRotation,
          duration: 200,
          useNativeDriver: false,
        })
      );

      nextSections[id] = { ...nextSections[id], isOpen: !isOpen };

      Animated.parallel(animations).start(() => {
        isAnimatingRef.current = false;
        setSectionsById(nextSections);
        // Re-run height sync so remaining open section(s) fill the space
        setOpenStateVersion((v) => v + 1);
      });
    },
    [accordionMode, sectionsById, heightsById]
  );

  const maxContentHeight = useMemo(
    () => getMaxContentHeight(),
    [getMaxContentHeight]
  );

  /**
   * Render a single section (header + animated content).
   * Optimization:
   * - Only render heavy list content if the section has been opened at least once,
   *   except for the first section (index === 0) which renders by default.
   */
  const renderSectionItem = useCallback(
    (item: SectionData<SectionHeaderType, DataType>, index: number) => {
      const {
        sectionHeader,
        data: rawSectionData,
        hasNextPage = false,
        loading: sectionLoading = false,
      } = item;
      const id = sectionHeader.id;
      const sectionData = orderedSectionDataById[id] ?? rawSectionData;

      const sectionState = sectionsById[id];
      const isOpen = sectionState
        ? sectionState.isOpen
        : (initialExpandedSectionIds?.includes(id) ?? index === 0);

      const isLoading = !!loadingSections[id];
      const shouldRenderContent =
        openedSections[id] ||
        initialExpandedSectionIds?.includes(id) ||
        index === 0;

      const onLoadMore = () => {
        if (!onSectionEndReached) return;

        // Prevent duplicate requests while one is in-flight
        if (isLoading) return;

        // Only trigger when there is a next page
        if (!hasNextPage) return;

        // Trigger once per "bottom reach" cycle
        if (loadMoreTriggeredRef.current[id]) return;

        loadMoreTriggeredRef.current[id] = true;

        setLoadingSections((prev) => ({ ...prev, [id]: true }));

        const result = onSectionEndReached(sectionHeader);

        // Support both sync and async callbacks
        Promise.resolve(result)
          .catch(() => {})
          .finally(() => {
            setLoadingSections((prev) => ({ ...prev, [id]: false }));
          });
      };
      return (
        <Box
          key={id}
          borderWidth={1}
          mb='m'
          mx='m'
          backgroundColor={
            id === hoveredHeaderId && !isOpen ? 'grey01' : 'white'
          }
          borderColor='grey02'
          borderRadius='xs'
          accessibilityLabel={sectionHeader.id.toLowerCase()}
          // @ts-ignore - web-specific props
          onMouseEnter={() => {
            if (!isOpen) setHoveredHeaderId(id);
          }}
          // @ts-ignore - web-specific props
          onMouseLeave={() => setHoveredHeaderId(undefined)}>
          {/* Header row */}
          <TouchableOpacity
            onPress={() => animate(id)}
            disabled={isAnimatingRef.current}>
            <Box
              height={52}
              flexDirection='row'
              alignItems='center'
              paddingLeft='m'
              style={{ paddingRight: theme.spacing.m + theme.spacing.l }}>
              {renderSectionHeader(sectionHeader)}
              <Animated.View style={getRotationStyle(id)}>
                <Icon variant='l' name='ChevronDown' color='grey05' />
              </Animated.View>
            </Box>
          </TouchableOpacity>

          {/* Collapsible content */}
          <Animated.View
            style={[
              getAnimatedContentStyle(id),
              { maxHeight: maxContentHeight },
            ]}>
            {shouldRenderContent && (
              <Box
                style={{ flex: 1, minHeight: 0 }}
                mb={id !== TaskGroup.Current ? contentBottomMargin : 'none'}>
                {renderSectionContentHeader && (
                  <Box
                    onLayout={(e) => {
                      const height = e.nativeEvent.layout.height;
                      if (sectionHeaderHeights[id] !== height) {
                        setSectionHeaderHeights((prev) => ({
                          ...prev,
                          [id]: height,
                        }));
                      }
                    }}
                    backgroundColor='white'
                    style={
                      stickySectionContentHeader
                        ? ({
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                          } as any)
                        : undefined
                    }>
                    {renderSectionContentHeader(sectionHeader)}
                  </Box>
                )}
                {sectionLoading &&
                  (renderSectionLoadingComponent ? (
                    renderSectionLoadingComponent(sectionHeader)
                  ) : (
                    <Box py='l' alignItems='center' justifyContent='center'>
                      <ActivityIndicator
                        size='small'
                        color={theme.colors.textPrimary as string}
                      />
                    </Box>
                  ))}
                {!sectionLoading &&
                  showEmptyMessage &&
                  sectionData.length === 0 &&
                  renderEmptyComponent && <Box>{renderEmptyComponent(id)}</Box>}
                {!sectionLoading && (
                  <>
                    <SelectableDndKitListWeb<DataType>
                      draggable={draggable}
                      enableRightClickMenu={!disableContextMenu}
                      onRightMenuAction={onRightMenuAction}
                      data={sectionData}
                      onOrderChange={(
                        newData,
                        { movedIds, afterId, beforeId }
                      ) => {
                        const previousData = sectionData;
                        setOrderedSectionDataById((prev) => ({
                          ...prev,
                          [id]: newData,
                        }));
                        Promise.resolve(
                          onOrderChange?.({
                            sectionHeader,
                            oldData: sectionData,
                            newData,
                            movedIds,
                            afterId,
                            beforeId,
                          })
                        ).catch((error) => {
                          console.error(
                            'Failed to handle section order change',
                            error
                          );
                          setOrderedSectionDataById((prev) => ({
                            ...prev,
                            [id]: previousData,
                          }));
                        });
                      }}
                      hasMore={hasNextPage}
                      isLoadingMore={isLoading}
                      loadMoreThresholdPx={200}
                      onLoadMore={onLoadMore}
                      renderItem={renderItem}
                    />

                    {/* Loading indicator */}
                    {isLoading && (
                      <Box
                        py='s'
                        flexDirection='row'
                        alignItems='center'
                        justifyContent='center'>
                        <ActivityIndicator
                          size='small'
                          color={theme.colors.grey04 as string}
                        />
                        <Text variant='webSmall' color='grey04' marginLeft='xs'>
                          Loading more ...
                        </Text>
                      </Box>
                    )}
                  </>
                )}

                {/* NewTaskItem */}
                {id === TaskGroup.Current && isOpen && (
                  <Box>{renderNewTaskItem()}</Box>
                )}
              </Box>
            )}
          </Animated.View>

          {/* Optional section footer */}
          {renderSectionFooterComponent?.(sectionHeader)}
        </Box>
      );
    },
    [
      animate,
      getAnimatedContentStyle,
      getRotationStyle,
      hoveredHeaderId,
      loadingSections,
      maxContentHeight,
      onRightMenuAction,
      onOrderChange,
      onSectionEndReached,
      openedSections,
      orderedSectionDataById,
      renderItem,
      renderNewTaskItem,
      renderSectionFooterComponent,
      renderSectionContentHeader,
      showEmptyMessage,
      renderSectionHeader,
      sectionsById,
      renderEmptyComponent,
      renderSectionLoadingComponent,
      stickySectionContentHeader,
      sectionHeaderHeights,
      disableContextMenu,
      initialExpandedSectionIds,
    ]
  );

  const renderGlobalHeader = useCallback(() => {
    if (!ListHeaderComponent) return null;
    if (typeof ListHeaderComponent === 'function') {
      const Comp = ListHeaderComponent as React.ComponentType<any>;
      return <Comp />;
    }
    return ListHeaderComponent;
  }, [ListHeaderComponent]);

  const renderGlobalFooter = useCallback(() => {
    if (!ListFooterComponent) return null;
    if (typeof ListFooterComponent === 'function') {
      const Comp = ListFooterComponent as React.ComponentType<any>;
      return <Comp />;
    }
    return ListFooterComponent;
  }, [ListFooterComponent]);

  return (
    <Box
      flex={1}
      // Measure container height so we can cap each expanded section height
      onLayout={(e) => {
        setContainerHeight(e.nativeEvent.layout.height);
      }}>
      {renderGlobalHeader()}

      {/* Render all sections (no outer FlatList scrolling) */}
      {data.map((sectionItem, index) => renderSectionItem(sectionItem, index))}

      {renderGlobalFooter()}
    </Box>
  );
};
